import { useEffect, useRef, useState } from 'react';
import { callApi } from './api';
import GenerationPanel from './GenerationPanel';

/**
 * 担当者会議録用：部署を選び、文字起こしを貼り付けて生成する画面。
 * この画面だけはユーザー指定によりプロンプト編集にパスワードをかけない
 * （各種会議用(ConferenceScreen)は引き続き管理者パスワードで保護したまま）。
 */
export default function MeetingScreen({ onBack }) {
  const [templates, setTemplates] = useState(null); // null=読み込み中
  const [loadError, setLoadError] = useState('');
  const [selectedId, setSelectedId] = useState('');

  const [managing, setManaging] = useState(false);

  const [prompts, setPrompts] = useState({}); // templateId -> 編集中の文面
  const [savingId, setSavingId] = useState('');
  const [savedId, setSavedId] = useState('');
  const [saveErrors, setSaveErrors] = useState({});
  const savingRef = useRef('');

  useEffect(() => {
    let cancelled = false;
    callApi('config')
      .then((data) => {
        if (cancelled) return;
        const meetings = (data.templates || []).filter((t) => t.kind === 'meeting');
        setTemplates(meetings);
        // 既に編集中の文面があれば上書きしない（再読み込みしても書きかけを消さない）。
        setPrompts((prev) => {
          const next = { ...prev };
          meetings.forEach((t) => { if (!(t.id in next)) next[t.id] = t.prompt; });
          return next;
        });
      })
      .catch((err) => { if (!cancelled) setLoadError(err.message); });
    return () => { cancelled = true; };
  }, []);

  const selectedTemplate = templates?.find((t) => t.id === selectedId);

  function handleChangePrompt(id, value) {
    setPrompts((prev) => ({ ...prev, [id]: value }));
  }

  async function handleSave(id) {
    if (savingRef.current === id) return;
    savingRef.current = id;
    setSavingId(id);
    setSaveErrors((prev) => ({ ...prev, [id]: '' }));
    try {
      await callApi('meeting_save', { templateId: id, prompt: prompts[id] });
      setSavedId(id);
      setTimeout(() => setSavedId((k) => (k === id ? '' : k)), 2000);
    } catch (err) {
      setSaveErrors((prev) => ({ ...prev, [id]: err.message }));
    } finally {
      savingRef.current = '';
      setSavingId('');
    }
  }

  return (
    <div className="workspace is-wide">
      <div className="workspace-head reveal r1">
        <button className="back-link" type="button" onClick={onBack}>← ホームへ戻る</button>
        <h1 className="workspace-title">担当者会議録用</h1>
      </div>

      <div className="notice reveal r2">
        利用者様・ご家族のお名前、住所、電話番号、被保険者番号は入力しないでください。
        貼り付ける前に、氏名は「A様」「B様」などに置き換えてください。
      </div>

      <div className="conf-mode-row reveal r2">
        <button
          type="button"
          className="admin-link"
          onClick={() => setManaging((m) => !m)}
        >
          {managing ? '編集を終える' : '⚙ プロンプトを編集する'}
        </button>
      </div>

      {templates === null && !loadError && (
        <p className="workspace-loading reveal r3">部署を読み込んでいます…</p>
      )}
      {loadError && <p className="pin-error reveal r3">{loadError}</p>}

      {templates && templates.length > 0 && (
        <div className="dept-grid reveal r3" role="group" aria-label="部署を選択">
          {templates.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`dept-pill${t.id === selectedId ? ' is-selected' : ''}`}
              onClick={() => setSelectedId(t.id)}
            >
              {t.display_name}
            </button>
          ))}
        </div>
      )}

      {!managing && selectedTemplate && (
        <GenerationPanel
          key={selectedTemplate.id}
          templateId={selectedTemplate.id}
          templateLabel={selectedTemplate.display_name}
        />
      )}

      {managing && (
        selectedTemplate ? (
          <div className="editor-card reveal" key={selectedTemplate.id}>
            <h2 className="admin-dept-title">{selectedTemplate.display_name}</h2>
            <textarea
              className="editor-textarea admin-prompt-textarea"
              value={prompts[selectedTemplate.id] ?? ''}
              onChange={(e) => handleChangePrompt(selectedTemplate.id, e.target.value)}
            />
            <div className="admin-save-row">
              <button
                className="enter admin-save-btn"
                type="button"
                disabled={savingId === selectedTemplate.id}
                onClick={() => handleSave(selectedTemplate.id)}
              >
                {savingId === selectedTemplate.id ? '保存中…' : 'この部署のプロンプトを保存'}
              </button>
              {savedId === selectedTemplate.id && <span className="admin-saved-label">保存しました</span>}
            </div>
            {saveErrors[selectedTemplate.id] && <p className="pin-error" role="alert">{saveErrors[selectedTemplate.id]}</p>}
          </div>
        ) : (
          <p className="workspace-loading reveal r4">編集する部署を選んでください。</p>
        )
      )}
    </div>
  );
}
